import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { IntlProvider, useIntl } from "react-intl";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import IntlMessages from "../utils/IntlMessages";
import { authenticateUser } from "../api/authApi";
import { getLoginLocale, useLoginSession } from "./loginHooks";
import "./Login.scss";

const validationSchema = Yup.object().shape({
  emailAddress: Yup.string().required(<IntlMessages id="user.emailRequired" />),
  password: Yup.string().required(<IntlMessages id="user.passwordRequired" />),
});

const initialValues = { emailAddress: "", password: "" };

const AdminIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 2L4 6v6c0 5.25 3.5 10.2 8 11.5C16.5 22.2 20 17.25 20 12V6l-8-4z" stroke="#586377" strokeWidth="1.5" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.5" stroke="#586377" strokeWidth="1.5" />
    <path d="M7.5 18c0-2.485 2.015-4.5 4.5-4.5s4.5 2.015 4.5 4.5" stroke="#586377" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const AdminLoginScreen = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  useLoginSession();

  const [showPw, setShowPw] = useState(false);

  const onSubmit = (values) => {
    dispatch(
      authenticateUser({
        email: `${values.emailAddress}@taeurope.com`,
        password: values.password,
      })
    );
  };

  return (
    <main className="ta-admin-login">
      <div className="ta-admin-login__panel">
        <div className="ta-login__card">
          <div className="ta-login__card-title">
            <AdminIcon />
            <span>{intl.formatMessage({ id: "login.adminTitle" })}</span>
          </div>
          <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
            {({ errors, touched }) => (
              <Form className="ta-login__form">
                <div className="ta-login__field">
                  <label className="ta-login__label" htmlFor="admin_email">
                    {intl.formatMessage({ id: "login.emailLabel" })}
                  </label>
                  <div className="ta-login__input-wrap">
                    <Field
                      name="emailAddress"
                      id="admin_email"
                      className={`ta-login__input ta-login__input--suffix${
                        touched.emailAddress && errors.emailAddress ? " ta-login__input--error" : ""
                      }`}
                      placeholder={intl.formatMessage({ id: "login.emailPlaceholder" })}
                      autoComplete="username"
                    />
                    <span className="ta-login__domain">@taeurope.com</span>
                  </div>
                  {touched.emailAddress && errors.emailAddress && (
                    <span className="ta-login__error">{errors.emailAddress}</span>
                  )}
                </div>

                <div className="ta-login__field">
                  <label className="ta-login__label" htmlFor="admin_password">
                    {intl.formatMessage({ id: "user.password" })}
                  </label>
                  <div className="ta-login__input-wrap">
                    <Field
                      name="password"
                      id="admin_password"
                      type={showPw ? "text" : "password"}
                      className={`ta-login__input ta-login__input--password${
                        touched.password && errors.password ? " ta-login__input--error" : ""
                      }`}
                      placeholder="••••••••"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="ta-login__eye-btn"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={intl.formatMessage({
                        id: showPw ? "login.hidePassword" : "login.showPassword",
                      })}
                    >
                      {showPw ? (
                        <VisibilityIcon sx={{ fontSize: 18, color: "#75777e" }} />
                      ) : (
                        <VisibilityOffIcon sx={{ fontSize: 18, color: "#75777e" }} />
                      )}
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <span className="ta-login__error">{errors.password}</span>
                  )}
                </div>

                <button type="submit" className="ta-login__submit">
                  {intl.formatMessage({ id: "login.submit" })}
                </button>
                <div className="ta-login__forgot">
                  <Link to="/login/forgot-password">
                    <IntlMessages id="user.forgotPassword" />
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        </div>
        <p className="ta-admin-login__back">
          <Link to="/login">{intl.formatMessage({ id: "login.backToSso" })}</Link>
        </p>
      </div>
    </main>
  );
};

const AdminLogin = () => {
  const [locale] = useState(() => getLoginLocale());

  return (
    <IntlProvider locale={locale.locale} messages={locale.messages}>
      <AdminLoginScreen />
    </IntlProvider>
  );
};

export default AdminLogin;
